'use client';

import { useEffect, useRef, useState } from 'react';
import { Send, Mail, MapPin, ArrowRight, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { notificationService } from '@/services/notificationService';
import type { ContactFormData } from '@/types/notifications';

const ContactForm = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  // Form state
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    company: '',
    propertyCount: '',
    interestType: undefined,
    message: '',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInterestChange = (value: 'full-service' | 'saas' | 'not-sure') => {
    setFormData(prev => ({ ...prev, interestType: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await notificationService.submitContactForm({
        ...formData,
        source: 'landing_contact_form',
      });

      if (result.success) {
        setIsSubmitted(true);
      } else {
        setError(result.error || 'Failed to submit form. Please try again.');
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      ref={sectionRef}
      id="contact"
      className="relative py-16 sm:py-24 lg:py-32 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-500/[0.03] to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-10 sm:mb-16">
          <span
            className={cn(
              "inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase",
              "bg-primary-500/10 text-primary-400 border border-primary-500/20 mb-4",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
          >
            Get Started
          </span>
          <h2
            className={cn(
              "font-playfair text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-secondary-100 mb-4",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '100ms' }}
          >
            Ready to Get Your
            <br />
            <span className="text-primary-400">Time Back?</span>
          </h2>
          <p
            className={cn(
              "max-w-2xl mx-auto text-lg text-gray-500 dark:text-secondary-400",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '200ms' }}
          >
            Tell us about your properties. We'll show you exactly how we can help.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 sm:gap-10 lg:gap-16">
          {/* Contact Form */}
          <div
            className={cn(
              "lg:col-span-3",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            )}
            style={{ transitionDelay: '300ms' }}
          >
            {isSubmitted ? (
              <div className="p-12 rounded-2xl bg-gradient-to-br from-success-500/10 to-transparent border border-success-500/20 text-center">
                <div className="w-16 h-16 rounded-full bg-success-500/20 border border-success-500/30 flex items-center justify-center mx-auto mb-6">
                  <Send className="w-8 h-8 text-success-400" />
                </div>
                <h3 className="text-2xl font-playfair font-bold text-gray-900 dark:text-secondary-100 mb-2">
                  Message Sent!
                </h3>
                <p className="text-gray-500 dark:text-secondary-400">
                  We'll be in touch within 24 hours to discuss your needs.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-4 rounded-xl bg-danger-500/10 border border-danger-500/20 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-danger-400 flex-shrink-0" />
                    <p className="text-sm text-danger-300">{error}</p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-secondary-300 mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="John Smith"
                      className={cn(
                        "w-full px-4 py-3 rounded-xl text-gray-900 dark:text-secondary-100 placeholder:text-gray-400 dark:placeholder:text-secondary-600",
                        "bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]",
                        "focus:outline-none focus:border-primary-500/30 focus:bg-gray-50 dark:focus:bg-white/[0.05]",
                        "transition-all duration-300"
                      )}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-secondary-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="john@company.com"
                      className={cn(
                        "w-full px-4 py-3 rounded-xl text-gray-900 dark:text-secondary-100 placeholder:text-gray-400 dark:placeholder:text-secondary-600",
                        "bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]",
                        "focus:outline-none focus:border-primary-500/30 focus:bg-gray-50 dark:focus:bg-white/[0.05]",
                        "transition-all duration-300"
                      )}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-secondary-300 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Acme Properties"
                      className={cn(
                        "w-full px-4 py-3 rounded-xl text-gray-900 dark:text-secondary-100 placeholder:text-gray-400 dark:placeholder:text-secondary-600",
                        "bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]",
                        "focus:outline-none focus:border-primary-500/30 focus:bg-gray-50 dark:focus:bg-white/[0.05]",
                        "transition-all duration-300"
                      )}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-secondary-300 mb-2">
                      Number of Properties
                    </label>
                    <select
                      name="propertyCount"
                      value={formData.propertyCount}
                      onChange={handleInputChange}
                      className={cn(
                        "w-full px-4 py-3 rounded-xl text-gray-900 dark:text-secondary-100",
                        "bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]",
                        "focus:outline-none focus:border-primary-500/30 focus:bg-gray-50 dark:focus:bg-white/[0.05]",
                        "transition-all duration-300"
                      )}
                    >
                      <option value="" className="bg-white dark:bg-[#1a1a1a]">Select...</option>
                      <option value="1-5" className="bg-white dark:bg-[#1a1a1a]">1-5 properties</option>
                      <option value="6-20" className="bg-white dark:bg-[#1a1a1a]">6-20 properties</option>
                      <option value="21-50" className="bg-white dark:bg-[#1a1a1a]">21-50 properties</option>
                      <option value="50+" className="bg-white dark:bg-[#1a1a1a]">50+ properties</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-secondary-300 mb-2">
                    I'm Interested In
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    {[
                      { value: 'full-service' as const, label: 'Full Service' },
                      { value: 'saas' as const, label: 'Axis SaaS' },
                      { value: 'not-sure' as const, label: 'Not Sure Yet' },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={cn(
                          "flex items-center justify-center px-4 py-3 rounded-xl cursor-pointer",
                          "bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]",
                          "hover:bg-gray-50 dark:hover:bg-white/[0.05] hover:border-gray-300 dark:hover:border-white/[0.12]",
                          formData.interestType === option.value && "bg-primary-500/10 border-primary-500/30",
                          "transition-all duration-300"
                        )}
                        onClick={() => handleInterestChange(option.value)}
                      >
                        <input
                          type="radio"
                          name="interest"
                          value={option.value}
                          checked={formData.interestType === option.value}
                          onChange={() => handleInterestChange(option.value)}
                          className="sr-only"
                        />
                        <span className="text-sm text-gray-600 dark:text-secondary-300">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-secondary-300 mb-2">
                    Tell Us About Your Needs
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="What challenges are you facing? What would success look like?"
                    className={cn(
                      "w-full px-4 py-3 rounded-xl text-gray-900 dark:text-secondary-100 placeholder:text-gray-400 dark:placeholder:text-secondary-600 resize-none",
                      "bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]",
                      "focus:outline-none focus:border-primary-500/30 focus:bg-gray-50 dark:focus:bg-white/[0.05]",
                      "transition-all duration-300"
                    )}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={cn(
                    "group w-full flex items-center justify-center gap-2 py-4 px-6 rounded-xl font-semibold",
                    "bg-primary-500 text-white",
                    "hover:bg-primary-400 transition-all duration-300",
                    "shadow-[0_0_40px_-10px_rgba(59,130,246,0.4)]",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div
            className={cn(
              "lg:col-span-2",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            )}
            style={{ transitionDelay: '400ms' }}
          >
            <div className="space-y-6">
              <div className="p-6 rounded-2xl bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06]">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-secondary-100 mb-4">Get in Touch</h3>

                <div className="space-y-4">
                  <a
                    href="mailto:hello@axisdatum.com"
                    className="flex items-center gap-3 text-gray-500 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 border border-primary-500/20 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <span>hello@axisdatum.com</span>
                  </a>

                  <div className="flex items-center gap-3 text-gray-500 dark:text-secondary-400">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 border border-primary-500/20 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <span>Dallas-Fort Worth, TX</span>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-primary-50 dark:bg-primary-500/10 border border-primary-500/20">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-secondary-100 mb-2">Office Hours</h3>
                <p className="text-gray-500 dark:text-secondary-400 text-sm mb-4">
                  We respond to all inquiries within 24 hours.
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-500 dark:text-secondary-400">
                    <span>Monday - Friday</span>
                    <span className="text-gray-700 dark:text-secondary-300">8am - 6pm CT</span>
                  </div>
                  <div className="flex justify-between text-gray-500 dark:text-secondary-400">
                    <span>Emergency Line</span>
                    <span className="text-primary-600 dark:text-primary-400">24/7/365</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { ContactForm };
